import React, { useRef, useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/system';
import { useMediaQuery } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

import {
  selectContext,
  selectSelectedIds,
  exitSelectionMode,
  setPageSize,
} from '../../redux/features/context/contextSlice';
import useGridLayout from '../../hooks/useGridLayout';
import useZenMode, { useZenShortcuts } from '../../hooks/useZenMode';
import { createItem } from '../../redux/features/items/itemsSlice';
import { fetchUserInfo, logoutUser, loginWithGoogle } from '../../redux/features/usersSlice';
import {
  fetchPendingCounts, selectPendingTotal, selectPendingCountsLoaded,
  selectModerationActive, setModerationActive,
} from '../../redux/features/pending/pendingSlice';
import { setSnackbar } from '../../redux/features/snackbarSlice';
import { mergeAuthor, mergeSite, mustSignIn } from '../../common/ItemTools';
import { BrandLink, BrandTitle, MenuIconButton } from '../common/appbar/primitives';
import { BORDER } from '../common/tokens';
import ViewDrawer from '../ViewDrawer';
import ImportDialog from '../ImportDialog';
import AddItemToListDialog from '../AddItemToListDialog';
import SettingsDialog from '../SettingsDialog';

import AccountMenu from './AccountMenu';
import ExpandingBox from './ExpandingBox';
import NavTabs from './NavTabs';
import SortSelect from './SortSelect';
import useEscapeToClose from '../common/useEscapeToClose';
import {
  AddChip, AddIconButton, ModerationChip, SearchIconButton, StyledAppBar,
} from './styles';

const TOOLBAR_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  width: '100%',
  height: '100%',
  position: 'relative',
  px: '14px',
};

const GROUP_SX = { display: 'flex', alignItems: 'center', gap: '12px', flex: '0 0 auto' };

const TAB_DIVIDER_SX = {
  width: '1px',
  height: '20px',
  margin: '0 -8px',
  background: BORDER,
  flex: '0 0 auto',
};

const ZOOMABLE_TYPES = ['items', 'authors', 'lists', 'sites'];

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const showTabs = useMediaQuery('(min-width:1400px)');
  const showButtonLabels = useMediaQuery('(min-width:900px)');
  const { pageSize, pageSizeOptions } = useGridLayout();
  const zenMode = useZenMode();
  const { userInfo } = useSelector((state) => state.users);
  const context = useSelector(selectContext);
  const pendingTotal = useSelector(selectPendingTotal);
  const pendingCountsLoaded = useSelector(selectPendingCountsLoaded);
  const moderationActive = useSelector(selectModerationActive);
  const isAuthorContext = context?.type === 'authors';
  const isSiteContext = context?.type === 'sites';
  const isListContext = context?.type === 'lists';
  const pageEntityIds = React.useMemo(() => {
    if (isAuthorContext) {
      return (context?.authors || []).map(mergeAuthor).filter(Boolean).map((a) => a.id);
    }
    if (isSiteContext) {
      return (context?.sites || []).map(mergeSite).filter(Boolean).map((s) => s.id);
    }
    if (isListContext) {
      return (context?.lists || []).map((list) => list.id);
    }
    return (context?.items || []).map((item) => item.id);
  }, [context, isAuthorContext, isSiteContext, isListContext]);

  const isModerator = !!userInfo?.isModerator;
  const isModerate = location.pathname.startsWith('/moderate');
  // Nothing to moderate -> no moderation UI at all.
  const hasPending = pendingTotal > 0;
  const moderationMode = isModerator && hasPending && (moderationActive || isModerate);

  // Keep the review badge/gate live: on load for any admin, and again on
  // entering the queue (where an approve/reject may have changed the totals).
  React.useEffect(() => {
    if (isModerator) {
      dispatch(fetchPendingCounts());
    }
  }, [isModerator, isModerate, dispatch]);

  // Queue drained while moderating -> leave the (now hidden) moderate views.
  // Wait for counts to load so a genuinely non-empty queue isn't bounced.
  React.useEffect(() => {
    if (isModerate && isModerator && pendingCountsLoaded && pendingTotal === 0) {
      dispatch(setModerationActive(false));
      navigate('/explore', { replace: true });
    }
  }, [isModerate, isModerator, pendingCountsLoaded, pendingTotal, dispatch, navigate]);

  // The review button summons moderation and dissolves it: click to enter
  // /moderate/items, click again to leave and clear the sticky flag.
  const handleModerationToggle = () => {
    if (moderationMode) {
      dispatch(setModerationActive(false));
      if (isModerate) navigate('/explore');
    } else {
      dispatch(setModerationActive(true));
      navigate('/moderate/items');
    }
  };

  const handleSourceChange = (newSource) => {
    const newParams = new URLSearchParams(searchParams);
    // cl is default
    if (newSource && newSource !== 'cl') {
      newParams.set('source', newSource);
    } else {
      newParams.delete('source');
    }
    newParams.delete('sortby');
    newParams.delete('page');
    navigate({ search: newParams.toString() });
  };

  const handleSortChange = (newSortby) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sortby', newSortby);
    newParams.delete('page');
    navigate({ search: newParams.toString() });
  };

  const { filter, type } = context || {};

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const handleDrawerClose = () => {
    setDrawerOpen(!drawerOpen);
  };

  const onLoginSuccess = async (codeResponse) => {
    dispatch(loginWithGoogle({
      code: codeResponse.code,
      redirectUri: window.location.origin,
    }))
      .unwrap()
      .then(() => {
        dispatch(fetchUserInfo());
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to log in.',
        }));
      });
  };

  const onLoginFailure = () => {
    dispatch(setSnackbar({
      snackbarOpen: true,
      snackbarType: 'error',
      snackbarMessage: 'Failed to log in.',
    }));
  };

  const handleLogout = () => {
    setDrawerOpen(false);
    dispatch(logoutUser());
  };

  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const [bulkAddToListOpen, setBulkAddToListOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const bulkAddItemIds = useSelector(selectSelectedIds);

  const routeNamespace = location.pathname.startsWith('/explore') || location.pathname.startsWith('/moderate') ? 'public' : 'private';
  const routePage = parseInt(searchParams.get('page') || '1', 10);
  const forceSignIn = mustSignIn(routeNamespace, routePage, userInfo);

  const [searchBoxEnabled, setSearchEnabled] = useState(!!filter);
  const searchEnabled = searchBoxEnabled || filter;
  const [searchValue, setSearchValue] = useState(filter || '');

  const [addBoxEnabled, setAddBoxEnabled] = useState(false);
  const [addUrlValue, setAddUrlValue] = useState('');
  const addInputRef = useRef(null);

  // The two boxes are mutually exclusive: opening either closes the other.
  const openSearchBox = React.useCallback(() => {
    setSearchEnabled(true);
    setAddBoxEnabled(false);
    setAddUrlValue('');
  }, []);

  const openAddBox = React.useCallback(() => {
    setAddBoxEnabled(true);
    setSearchEnabled(false);
  }, []);

  // Registered here because the bar stays mounted while hidden - it is what
  // listens itself back into view.
  useZenShortcuts();

  // 'c' opens the add-item box, 's' opens search — each closes the other.
  // Both are silent while the box they open has nowhere to appear.
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (zenMode) return;
      if (e.key === 'c') {
        e.preventDefault();
        openAddBox();
      }
      if (e.key === 's') {
        e.preventDefault();
        openSearchBox();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [openAddBox, openSearchBox, zenMode]);

  const [viewType, setViewType] = useState();
  if (type !== viewType) {
    setSearchEnabled(false);
    setSearchValue(filter || '');
    setViewType(type);
  }

  React.useEffect(() => {
    setSearchValue(filter || '');
    if (!filter) {
      setSearchEnabled(false);
    }
  }, [filter]);

  React.useEffect(() => {
    if (addBoxEnabled && addInputRef.current) {
      addInputRef.current.focus();
    }
  }, [addBoxEnabled]);

  const onCancelSearch = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('filter');
    newParams.delete('page');
    navigate({ search: newParams.toString() });
    setSearchEnabled(false);
    setSearchValue('');
  };

  const closeAddBox = () => {
    setAddBoxEnabled(false);
    setAddUrlValue('');
  };

  useEscapeToClose(searchEnabled && !zenMode, onCancelSearch);
  useEscapeToClose(addBoxEnabled && !zenMode, closeAddBox);

  const signIn = useGoogleLogin({
    onSuccess: onLoginSuccess,
    onError: onLoginFailure,
    flow: 'auth-code',
  });

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancelSearch();
      return;
    }
    if (e.key === 'Enter') {
      const newParams = new URLSearchParams(searchParams);
      if (searchValue) {
        newParams.set('filter', searchValue);
      } else {
        newParams.delete('filter');
        setSearchEnabled(false);
      }
      newParams.delete('page'); // Reset to page 1 when searching
      navigate({ search: newParams.toString() });
    }
  };

  const handleSearchIconClick = () => {
    if (!searchValue) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.set('filter', searchValue);
    newParams.delete('page');
    navigate({ search: newParams.toString() });
  };

  const handleAddSubmit = () => {
    if (!addUrlValue || !addUrlValue.trim()) return;

    const trimmedUrl = addUrlValue.trim();

    try {
      const urlObject = new URL(trimmedUrl);
      if (!urlObject.protocol || !['http:', 'https:'].includes(urlObject.protocol)) {
        throw new Error('Invalid protocol');
      }
      if (!urlObject.hostname) {
        throw new Error('Missing hostname');
      }
    } catch (e) {
      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'error',
        snackbarMessage: 'Please enter a valid URL starting with http:// or https://',
      }));
      return;
    }

    const newItem = { links: [{ url: trimmedUrl, primary: true }] };
    dispatch(createItem({ namespace: 'private', item: newItem }))
      .unwrap()
      .then(() => {
        window.dispatchEvent(new CustomEvent('itemsChanged'));
        closeAddBox();
      })
      .catch((err) => {
        const duplicateItemId = err?.code === 'DUPLICATE_ITEM_URL'
          ? err?.details?.itemId
          : null;
        if (duplicateItemId) {
          dispatch(setSnackbar({
            snackbarOpen: true,
            snackbarType: 'error',
            snackbarMessage: 'This URL already exists in',
            snackbarLink: { text: 'an existing item.', to: `/items/${duplicateItemId}` },
          }));
        } else {
          dispatch(setSnackbar({
            snackbarOpen: true,
            snackbarType: 'error',
            snackbarMessage: 'Failed to create item. Please try again.',
          }));
        }
      });
  };

  const handleAddKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeAddBox();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleAddSubmit();
    }
  };

  // `pageSizeOptions` runs small to large, so "More" steps up and "Less" down.
  const zoomIndex = pageSizeOptions.indexOf(pageSize);
  const showZoom = showButtonLabels && !forceSignIn && ZOOMABLE_TYPES.includes(context?.type);

  if (zenMode) return null;

  return (
    <>
      <StyledAppBar elevation={0} position="fixed">
        <Box sx={TOOLBAR_SX}>
          {searchEnabled && (
            <ExpandingBox
              value={searchValue}
              placeholder="Search..."
              autoComplete="off"
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onCancel={onCancelSearch}
              action={(
                <SearchIconButton onClick={handleSearchIconClick} size="small">
                  <SearchIcon />
                </SearchIconButton>
              )}
            />
          )}
          {addBoxEnabled && (
            <ExpandingBox
              inputRef={addInputRef}
              value={addUrlValue}
              placeholder="Enter URL..."
              onChange={(e) => setAddUrlValue(e.target.value)}
              onKeyDown={handleAddKeyDown}
              onCancel={closeAddBox}
              action={(
                <AddIconButton onClick={handleAddSubmit} size="small">
                  +
                </AddIconButton>
              )}
            />
          )}
          <Box sx={GROUP_SX}>
            <MenuIconButton onClick={handleDrawerClose}>
              <MenuIcon />
            </MenuIconButton>
            <BrandLink to="/explore">
              <BrandTitle color="primary">Curated</BrandTitle>
              <BrandTitle color="secondary">List</BrandTitle>
            </BrandLink>
          </Box>
          {showTabs && (
            <>
              <Box sx={{
                display: 'flex', alignItems: 'center', flex: '0 0 auto', marginLeft: '-4px',
              }}>
                <NavTabs
                  pathname={location.pathname}
                  isModerate={isModerate}
                  moderationMode={moderationMode}
                  pendingTotal={pendingTotal}
                />
              </Box>
              <Box sx={TAB_DIVIDER_SX} />
            </>
          )}
          <Box sx={GROUP_SX}>
            {showButtonLabels && !forceSignIn && (
              <SortSelect context={context} onChange={handleSortChange} />
            )}
            {userInfo?.givenName && !isAuthorContext && (
              <AddChip onClick={() => (addBoxEnabled ? closeAddBox() : openAddBox())}>
                <AddIcon fontSize="small" />
                {showButtonLabels && <span>Add</span>}
              </AddChip>
            )}
            {!forceSignIn && (
              <AddChip onClick={() => (searchEnabled ? onCancelSearch() : openSearchBox())}>
                <SearchIcon fontSize="small" />
                {showButtonLabels && <span>Search</span>}
              </AddChip>
            )}
          </Box>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }} />
          <Box sx={{ ...GROUP_SX, gap: '2px' }}>
            {showZoom && (
              <>
                <AddChip
                  onClick={() => {
                    if (zoomIndex < pageSizeOptions.length - 1) {
                      dispatch(setPageSize(pageSizeOptions[zoomIndex + 1]));
                    }
                  }}
                  disabled={zoomIndex >= pageSizeOptions.length - 1}
                  title="Show more cards"
                >
                  <ZoomInIcon fontSize="small" />
                  <span>More</span>
                </AddChip>
                <AddChip
                  onClick={() => {
                    if (zoomIndex > 0) {
                      dispatch(setPageSize(pageSizeOptions[zoomIndex - 1]));
                    }
                  }}
                  disabled={zoomIndex <= 0}
                  title="Show fewer cards"
                >
                  <ZoomOutIcon fontSize="small" />
                  <span>Less</span>
                </AddChip>
              </>
            )}
            {isModerator && hasPending && (
              <ModerationChip
                type="button"
                active={moderationMode}
                onClick={handleModerationToggle}
                title={moderationMode ? 'Leave moderation' : 'Moderate pending'}
              >
                <LibraryAddCheckIcon sx={{ fontSize: 16 }} />
                <span>{pendingTotal}</span>
              </ModerationChip>
            )}
            <AccountMenu
              userInfo={userInfo}
              context={context}
              isModerate={isModerate}
              pageEntityIds={pageEntityIds}
              onSignIn={signIn}
              onSettings={() => setSettingsOpen(true)}
              onImport={() => setImportDialogOpen(true)}
              onBulkAddToList={() => setBulkAddToListOpen(true)}
              onLogout={handleLogout}
              onSourceChange={handleSourceChange}
            />
          </Box>
        </Box>
      </StyledAppBar>
      <ViewDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
      <ImportDialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} />
      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <AddItemToListDialog
        open={bulkAddToListOpen}
        itemIds={bulkAddItemIds}
        onClose={() => {
          setBulkAddToListOpen(false);
          dispatch(exitSelectionMode());
        }}
      />
    </>
  );
}

export default Header;
